import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/** Marks a request that already retried after a token refresh (prevents refresh loops). */
const RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isAuthExempt(request.url)) {
      return next.handle(request);
    }

    const alreadyRetried = request.headers.has(RETRY_HEADER);

    // After long idle the access JWT is expired. Renew first so pages do not
    // all 401 at once and paint as "empty data".
    if (
      !alreadyRetried &&
      this.authService.getRefreshToken() &&
      (this.authService.isTokenExpired() || !this.authService.getToken())
    ) {
      return this.refreshAndRetry(request, next);
    }

    return this.sendWithToken(request, next, this.authService.getToken());
  }

  private sendWithToken(
    request: HttpRequest<any>,
    next: HttpHandler,
    token: string | null
  ): Observable<HttpEvent<any>> {
    const alreadyRetried = request.headers.has(RETRY_HEADER);
    let authRequest = request;
    if (token) {
      authRequest = this.addTokenToRequest(request, token);
    }

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }

        const hadSession =
          !!this.authService.getRefreshToken() || !!this.authService.getToken();

        // Guest / public browsing: do not treat 401 as "session invalid".
        if (!hadSession) {
          return throwError(() => error);
        }

        if (alreadyRetried || !this.authService.getRefreshToken()) {
          this.forceLogoutToLogin();
          return throwError(() => error);
        }

        return this.refreshAndRetry(request, next);
      })
    );
  }

  /**
   * Refresh once (single-flight in AuthService), then retry the original request.
   * Logout only when refresh itself fails — not when the retried API call fails.
   */
  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.authService.refreshAccessToken().pipe(
      catchError((refreshError) => {
        this.forceLogoutToLogin();
        return throwError(() => refreshError);
      }),
      switchMap((newToken: string) =>
        next.handle(
          this.addTokenToRequest(request, newToken).clone({
            setHeaders: { [RETRY_HEADER]: '1' },
          })
        )
      )
    );
  }

  private forceLogoutToLogin(): void {
    this.authService.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'session' } });
  }

  private isAuthExempt(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/admin-login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/send-verification') ||
      url.includes('/auth/verify-email') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password') ||
      url.includes('/student-theme') ||
      url.includes('/student-portal-branding') ||
      url.includes('/faqs') ||
      url.includes('/reschedule-configuration') ||
      url.includes('/courses/customer-portal') ||
      url.includes('/paymentmethods')
    );
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
